const _ = require("lodash");

// This file contains a function creating a fixed-dimension Vector class +
// functions + methods. It also exports premade 2D and 3D versions under the
// names V2 and V3, and it exports a function that can create an extended
// class.

// Returns true for vectors and arrays.
const isArrayLike = function(x) {
	return x && typeof x === "object" && typeof x.length === "number";
};

// Given an object of methods, each of whose first argument is a vector
// instance, attach it to the provided Vector class as both static and instance
// methods, where the instance methods have this as the first argument.
const attachMethods = function(VectorClass, vMethods) {
	for (const nameIterator in vMethods) {
		const name = nameIterator;
		VectorClass[name] = vMethods[name];
		VectorClass.prototype[name] = function(...args) {
			return vMethods[name](this, ...args);
		};
	}
};

// Create an n-dimensional Vector class. Acts almost exactly like an array,
// just has a constructor and a bunch of methods.
const makeNDVectorClass = function(n) {
	const Vector = function(...args) {
		const x = args[0];
		if (!(this instanceof Vector)) {
			return new Vector(...args);
		} else if (isArrayLike(x) && x.length >= n) {
			for (let k = 0; k < n; k += 1) {
				this[k] = x[k];
			}
		} else {
			for (let k = 0; k < n; k += 1) {
				this[k] = args[k] || 0;
			}
		}
	};
	Vector.prototype = _.fill(Array(n), 0);

	// Methods on vectors, sent to attachMethods at the end.
	const vMethods = {};

	// Arbitrary number of vectors accepted.
	// Also can take (v, x1, x2, ..., xn) and return v + [x1, x2, ..., xn].
	vMethods.add = function(v1, ...args) {
		const result = Vector(v1);
		if (typeof args[0] === "number" && typeof args[1] === "number") {
			for (let k = 0; k < n; k += 1) {
				result[k] += args[k];
			}
		} else {
			for (let i = 0; i < args.length; i += 1) {
				for (let k = 0; k < n; k += 1) {
					result[k] += args[i][k];
				}
			}
		}
		return result;
	};
	// Aliases
	vMethods.sum = vMethods.add;
	vMethods.plus = vMethods.add;
	vMethods.a = vMethods.add;

	vMethods.neg = function(v1) {
		const result = Vector(v1);
		for (let k = 0; k < n; k += 1) {
			result[k] = -result[k];
		}
		return result;
	};

	vMethods.sub = function(v1, ...args) {
		const result = Vector(v1);
		const v2 = args[0];
		if (isArrayLike(v2)) {
			for (let k = 0; k < n; k += 1) {
				result[k] -= v2[k];
			}
		} else {
			for (let k = 0; k < n; k += 1) {
				result[k] -= args[k];
			}
		}
		return result;
	};
	// Aliases
	vMethods.diff = vMethods.sub;
	vMethods.minus = vMethods.sub;

	vMethods.dot = function(v1, v2) {
		let result = 0;
		for (let k = 0; k < n; k += 1) {
			result += v1[k] * v2[k];
		}
		return result;
	};

	vMethods.magnitude = function(v1) {
		return Math.sqrt(v1.dot(v1));
	};
	// Aliases
	vMethods.abs = vMethods.magnitude;

	// Arbitrary number of scalars accepted after vector
	vMethods.scale = function(v1, ...args) {
		if (!isArrayLike(v1)) {
			throw new Error(
				"scale: first argument must be vector or array, got " + v1
			);
		}
		const result = Vector(v1);
		for (let i = 0; i < args.length; i += 1) {
			if (isFinite(args[i])) {
				const s = +args[i];
				for (let k = 0; k < n; k += 1) {
					result[k] *= s;
				}
			} else {
				throw new Error(
					"scale: arguments past first must be finite numbers, " +
						(i + 1) +
						"th was " +
						args[i]
				);
			}
		}
		return result;
	};
	// Aliases
	vMethods.coeff = vMethods.scale;
	vMethods.s = vMethods.scale;
	vMethods.c = vMethods.scale;

	vMethods.toMagnitude = function(v, newMagnitude) {
		const oldMagnitude = v.magnitude();
		return oldMagnitude > 0 ? v.scale(newMagnitude / oldMagnitude) : v;
	};
	// Aliases
	vMethods.toAbs = vMethods.toMagnitude;

	vMethods.unit = function(v) {
		return v.toMagnitude(1);
	};

	// Arbitrary number of vector, scalar argument pairs accepted.
	vMethods.linearCombination = function(v1, t, ...args) {
		let result = vMethods.s(v1, t);
		for (let i = 0; i < args.length; i += 2) {
			if (!isArrayLike(args[i])) {
				throw new Error(
					"linearCombination: odd arguments must be vectors or arrays, " +
						(i + 1) +
						"th was " +
						args[i]
				);
			} else if (!isFinite(args[i + 1])) {
				throw new Error(
					"linearCombination: even arguments must be finite numbers, " +
						(i + 2) +
						"th was " +
						args[i + 1]
				);
			}
			result = vMethods.a(result, vMethods.s(args[i], args[i + 1]));
		}
		return result;
	};
	// Aliases
	vMethods.linComb = vMethods.linearCombination;
	vMethods.comb = vMethods.linearCombination;
	vMethods.lc = vMethods.linearCombination;

	vMethods.interpolate = function(v1, v2, t) {
		return vMethods.lc(v1, t, v2, 1 - t);
	};
	// Aliases
	vMethods.int = vMethods.interpolate;
	vMethods.i = vMethods.interpolate;

	vMethods.midpoint = function(v1, v2) {
		return vMethods.lc(v1, 0.5, v2, 0.5);
	};
	// Aliases
	vMethods.mid = vMethods.midpoint;

	vMethods.average = function(...vs) {
		if (!vs.length) {
			return Vector();
		}
		return vMethods.add(...vs).scale(1 / vs.length);
	};
	// Aliases
	vMethods.centroid = vMethods.average;
	vMethods.avg = vMethods.average;

	vMethods.project = function(v1, v2) {
		const u2 = v2.unit();
		return u2.scale(v1.dot(u2));
	};

	// 2D and 3D versions of cross product.
	if (n === 2) {
		// 2D version of cross product that returns a scalar.
		vMethods.determinant = function(v1, v2) {
			return v1[0] * v2[1] - v1[1] * v2[0];
		};
		// Aliases
		vMethods.det = vMethods.determinant;
	} else if (n === 3) {
		vMethods.cross = function(v1, v2) {
			return Vector(
				v1[1] * v2[2] - v1[2] * v2[1],
				v1[2] * v2[0] - v1[0] * v2[2],
				v1[0] * v2[1] - v1[1] * v2[0]
			);
		};

		vMethods.faceNormal = function(v1, v2, v3) {
			return vMethods.cross(v2.sub(v1), v3.sub(v1));
		};
	}

	// Rotation functions in 2D and 3D.
	if (n === 2) {
		// Add (r,theta) to a vector
		vMethods.polarAdd = function(v1, r, theta) {
			const rot = (theta * Math.PI) / 180;
			return Vector(v1).add(r * Math.cos(rot), r * Math.sin(rot));
		};

		vMethods.rotate = function(v1, ang, vAbout) {
			vAbout = Vector(vAbout); // [0, 0] if no vAbout provided.
			v1 = vMethods.sub(v1, vAbout);
			const result = Vector();
			const cos = Math.cos(ang);
			const sin = Math.sin(ang);
			result[0] = v1[0] * cos + -v1[1] * sin;
			result[1] = v1[0] * sin + v1[1] * cos;
			return vMethods.add(result, vAbout);
		};
		// Aliases
		vMethods.rot = vMethods.rotate;

		vMethods.rotateDeg = function(v1, ang, vAbout) {
			return vMethods.rotate(v1, (ang * Math.PI) / 180, vAbout);
		};
		// Aliases
		vMethods.rotDeg = vMethods.rotateDeg;

		vMethods.intersectpoint = function(v1, v2, v3, v4) {
			if (
				!isArrayLike(v1) ||
				!isArrayLike(v2) ||
				!isArrayLike(v3) ||
				!isArrayLike(v4)
			) {
				throw new Error(
					"intersectpoint: all arguments must be vector or array"
				);
			}
			const denom =
				(v1[0] - v2[0]) * (v3[1] - v4[1]) - (v1[1] - v2[1]) * (v3[0] - v4[0]);
			if (denom === 0) {
				throw new Error(
					"intersectpoint: cannot find the intersection of parallel lines"
				);
			}
			const numX =
				(v1[0] * v2[1] - v1[1] * v2[0]) * (v3[0] - v4[0]) -
				(v1[0] - v2[0]) * (v3[0] * v4[1] - v3[1] * v4[0]);
			const numY =
				(v1[0] * v2[1] - v1[1] * v2[0]) * (v3[1] - v4[1]) -
				(v1[1] - v2[1]) * (v3[0] * v4[1] - v3[1] * v4[0]);
			return Vector(numX / denom, numY / denom);
		};
		//Aliases
		vMethods.ipoint = vMethods.intersectpoint;
	} else if (n === 3) {
		// 3D rotation, using a normal vector to determine plane of rotation.
		vMethods.rotateNormal = function(v1, ang, vNormal, vAbout) {
			vAbout = Vector(vAbout); // [0, 0, 0] if no vAbout provided.
			v1 = vMethods.sub(v1, vAbout);
			// Subtract normal component from v1 - vAbout, added back in later
			vNormal = Vector(vNormal).unit();
			const v1NormComp = v1.project(vNormal);
			v1 = vMethods.sub(v1, v1NormComp);
			const vPerp = vNormal.cross(v1);
			const cos = Math.cos(ang);
			const sin = Math.sin(ang);
			return vMethods.linearCombination(
				v1,
				cos,
				vPerp,
				sin,
				vAbout,
				1,
				v1NormComp,
				1
			);
		};
	}

	// Reflecting across a line (which only makes sense in 2D).
	if (n === 2) {
		vMethods.reflectLine = function(v1, vEndpoint1, vEndpoint2) {
			const line = vEndpoint2.minus(vEndpoint1);
			const unitNormal = line.rotDeg(90).unit();
			// Connect the given point to an arbitrary point on the line and
			// project it onto the unit normal.
			const pointToLine = v1.minus(vEndpoint2);
			const pointToLineNormal = pointToLine.project(unitNormal);
			// Go twice the distance to get the reflection.
			return v1.minus(pointToLineNormal.scale(2));
		};
	}

	vMethods.max = function(...args) {
		const result = Array(n);
		let vArray = args;
		if (isArrayLike(args[0]) && isArrayLike(args[0][0])) {
			vArray = args[0];
		}
		for (let k = 0; k < n; k += 1) {
			result[k] = _.max(_.map(vArray, vec => vec[k]));
		}
		return result;
	};

	vMethods.min = function(...args) {
		const result = Array(n);
		let vArray = args;
		if (isArrayLike(args[0]) && isArrayLike(args[0][0])) {
			vArray = args[0];
		}
		for (let k = 0; k < n; k += 1) {
			result[k] = _.min(_.map(vArray, vec => vec[k]));
		}
		return result;
	};

	attachMethods(Vector, vMethods);
	return Vector;
};

const makeExtendedVectorClass = function(n, vMethods) {
	const Vector = makeNDVectorClass(n);
	attachMethods(Vector, vMethods);
	return Vector;
};

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
module.exports = {
	makeNDVectorClass,
	makeExtendedVectorClass,
	V2: makeNDVectorClass(2),
	V3: makeNDVectorClass(3),
};
